import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Info, X } from "lucide-react";

const STORAGE_KEY = "vidalv_cookie_consent"; // värdet är endast "necessary" (dvs användaren har sett notisen)

/**
 * Cookie-/integritets-notis (lågmäld, ej blockerande).
 *
 * vidalv.live använder INGA marknadsförings-/spårnings-cookies. För
 * besöksstatistik kör vi PostHog i EU Cloud i ett cookielöst server-hash-läge
 * (inga cookies, ingen localStorage/sessionStorage-nyckel, inga personprofiler
 * och ingen identify). Därför visar vi en kort engångsnotis med länk till
 * integritets- och cookiepolicy — inte en formell consent-dialog.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "necessary");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="status"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[90] bg-parchment border border-warm-border shadow-xl rounded-sm"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-pine shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 id="cookie-banner-title" className="font-heading text-base font-semibold text-ink mb-1">
              Vi värnar din integritet
            </h2>
            <p className="text-sm text-bark leading-relaxed">
              vidalv.live använder <strong>inga spårnings- eller reklamcookies</strong>. För
              besöksstatistik kör vi PostHog i EU Cloud i ett <em>cookielöst server-hash-läge</em> –
              inga cookies, ingen localStorage, inga personprofiler. Bara nödvändiga cookies för
              inloggning och språkval används.{" "}
              <Link to="/integritetspolicy" className="text-falu underline">Integritetspolicy</Link>
              {" · "}
              <Link to="/cookiepolicy" className="text-falu underline">Cookiepolicy</Link>
            </p>
            <div className="mt-3">
              <button
                onClick={dismiss}
                data-testid="cookie-accept-necessary"
                className="px-4 py-2 text-sm font-body font-semibold bg-falu hover:bg-falu-hover text-parchment rounded-sm transition-colors"
              >
                Tack, jag förstår
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Stäng notis"
            className="text-bark/60 hover:text-ink p-1 shrink-0"
            data-testid="cookie-banner-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
